/**
 * useChannelBatch - state behind ChannelRowsPicker for adding every channel
 * of a multi-channel device (ConXagent) in one save.
 *
 * Call it at page / section level, not inside the dialog, so the
 * temperature differential pairs picked between channels (each channel's
 * checklist) are remembered for every later device and survive closing and
 * reopening the dialog. They are kept per `nameScope` (the hardware type),
 * as pairs of channel numbers.
 *
 * Channel names are the full location names ("Pier 3 Top"), so they are
 * cleared for each device: kept, they would clash with the device just
 * added. Only pairs whose two channels are both ticked
 * take effect, so unticking a channel with no probe leaves its pairs out for
 * this device without forgetting them for the next one.
 *
 * Partners already on the pour (another device's locations) are picked per
 * channel in the same checklist and kept by key (location ID on the edit
 * page, draft index in the wizard). They belong to this device only, so a
 * different device or a reset clears them.
 *
 * The unticked list is keyed by the device it was made for, so picking a
 * different device re-ticks every free channel without an effect.
 *
 * Each channel has its own Function (Temperature Only, Temperature Only
 * (ambient) or, on a maturity pour, Concrete Maturity), as each legacy
 * location had. Channels default to Concrete Maturity on a maturity pour,
 * else Temperature Only; the ones picked are remembered per scope for the
 * next device, like the pairs.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  buildChannelRows,
  channelBatchErrors,
  channelDiffPairs,
  ChannelDiffPair,
  ChannelRow,
  CHANNEL_NUMBERS,
  hasChannelDiffPair,
  PourChannelRef,
  siblingNoFromDiffId,
  toggleChannelDiffPair,
  usedChannelsOnLogger,
} from '../../utils/channelRows';
import type { LocationFunctionValue } from '../../utils/locationFunction';
import type { ChannelRowsPickerProps } from './ChannelRowsPicker';

export interface UseChannelBatchArgs {
  /** Selected device ('' when none is picked yet). */
  loggerId: string;
  /** Channels the device offers. Defaults to CHANNEL_NUMBERS (1-4). */
  channelNos?: ReadonlyArray<number>;
  /** Every location already on the pour (saved, or wizard drafts). */
  pourChannels: ReadonlyArray<PourChannelRef>;
  /** Names and remembered pairs are kept per scope, e.g. the hardware type. */
  nameScope: string;
  /** The pour lets concrete locations compute maturity: Concrete Maturity
   *  is one of each channel's Functions, and the default. */
  maturityAllowed?: boolean;
}

/** Props the hook fills in. The dialog adds onEnter, disabled,
 *  firstNameInputRef, showDifferentials and pourDiffOptions. */
export type ChannelBatchPickerProps = Pick<
  ChannelRowsPickerProps,
  | 'rows'
  | 'onChannelNameChange'
  | 'onCheckedChange'
  | 'deviceSelected'
  | 'showErrors'
  | 'isPairSelected'
  | 'pourPartnersFor'
  | 'onDiffChange'
  | 'onFunctionChange'
>;

export interface UseChannelBatchResult {
  rows: ChannelRow[];
  /** Ticked free rows, in channel order: what a save creates. */
  selected: ChannelRow[];
  showErrors: boolean;
  /** The picked pairs whose two channels are both ticked: what a save pairs
   *  up. Channel numbers, a < b, in channel order. */
  differentialPairs: ChannelDiffPair[];
  /** True when channels `a` and `b` (either order) are paired, ticked or
   *  not. */
  isPairSelected: (a: number, b: number) => boolean;
  /** Pairs or unpairs channels `a` and `b` (either order); remembered for
   *  the next device. */
  onPairToggle: (a: number, b: number) => void;
  /** Keys of the locations already on the pour that channel `no` is paired
   *  with (this device only). */
  pourPartnersFor: (no: number) => string[];
  /** Applies a channel's checklist: `ids` holds siblingDiffId(n) for other
   *  channels of this device and plain keys for pour locations. */
  onDiffChange: (no: number, ids: ReadonlyArray<string>) => void;
  /** Sets channel `no`'s Function; remembered for the next device. */
  onFunctionChange: (no: number, fn: LocationFunctionValue) => void;
  pickerProps: ChannelBatchPickerProps;
  /** The blocking messages for the rows as they are now, the same list
   *  validate returns, worked out without setting any state, so a dialog
   *  can keep its error list up to date while it renders. */
  errors: string[];
  /** Turns on the save-time errors and returns the blocking messages
   *  (empty when the batch can be saved). Returns [] while no device is
   *  picked; the dialog's own "Hardware Number is required." covers that. */
  validate: () => string[];
  /** Clears the names and pour partners, re-ticks every channel and hides
   *  save-time errors. Pairs between channels are kept. */
  resetForNextDevice: () => void;
  /** Same as resetForNextDevice; call it when the dialog opens fresh. */
  reset: () => void;
}

const EMPTY_NAMES: Record<number, string> = {};
const NO_PAIRS: ChannelDiffPair[] = [];
const NONE_UNTICKED: number[] = [];
const NO_POUR_PARTNERS: Record<number, string[]> = {};
const NO_KEYS: string[] = [];
const NO_FUNCTIONS: Record<number, LocationFunctionValue> = {};

export const useChannelBatch = ({
  loggerId,
  channelNos = CHANNEL_NUMBERS,
  pourChannels,
  nameScope,
  maturityAllowed = false,
}: UseChannelBatchArgs): UseChannelBatchResult => {
  const [namesByScope, setNamesByScope] = useState<Record<string, Record<number, string>>>({});
  const [pairsByScope, setPairsByScope] = useState<Record<string, ChannelDiffPair[]>>({});
  const [untickedFor, setUntickedFor] = useState<{ loggerId: string; nos: number[] }>({
    loggerId: '',
    nos: [],
  });
  const [pourPartnersOf, setPourPartnersOf] = useState<{ loggerId: string; byNo: Record<number, string[]> }>({
    loggerId: '',
    byNo: {},
  });
  const [showErrors, setShowErrors] = useState(false);
  const [functionsByScope, setFunctionsByScope] = useState<
    Record<string, Record<number, LocationFunctionValue>>
  >({});

  const channelNames = namesByScope[nameScope] || EMPTY_NAMES;
  const rememberedPairs = pairsByScope[nameScope] || NO_PAIRS;
  const functions = functionsByScope[nameScope] || NO_FUNCTIONS;
  // Unticks only apply to the device they were made for.
  const unticked = untickedFor.loggerId === loggerId ? untickedFor.nos : NONE_UNTICKED;
  // So do partners on the pour.
  const pourPartners = pourPartnersOf.loggerId === loggerId ? pourPartnersOf.byNo : NO_POUR_PARTNERS;
  const channelKey = channelNos.join(',');

  const rows = useMemo(
    () =>
      buildChannelRows({
        channelNos,
        used: usedChannelsOnLogger(pourChannels, loggerId),
        channelNames,
        unticked,
        existingNames: pourChannels.map((r) => r.name),
        maturityAllowed,
        functions,
      }),
    // channelKey stands in for channelNos so a fresh array literal each
    // render doesn't rebuild the rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [channelKey, pourChannels, loggerId, channelNames, unticked, maturityAllowed, functions],
  );

  const selected = useMemo(() => rows.filter((r) => r.checked), [rows]);

  const differentialPairs = useMemo(
    () => channelDiffPairs(rows, rememberedPairs),
    [rows, rememberedPairs],
  );

  const isPairSelected = useCallback(
    (a: number, b: number) => hasChannelDiffPair(rememberedPairs, a, b),
    [rememberedPairs],
  );

  const onChannelNameChange = useCallback(
    (no: number, value: string) => {
      setNamesByScope((prev) => ({
        ...prev,
        [nameScope]: { ...(prev[nameScope] || {}), [no]: value },
      }));
    },
    [nameScope],
  );

  const onCheckedChange = useCallback(
    (no: number, checked: boolean) => {
      setUntickedFor((prev) => {
        const current = prev.loggerId === loggerId ? prev.nos : [];
        const nos = checked
          ? current.filter((n) => n !== no)
          : current.includes(no)
            ? current
            : [...current, no];
        return { loggerId, nos };
      });
    },
    [loggerId],
  );

  const onPairToggle = useCallback(
    (a: number, b: number) => {
      if (a === b) return;
      setPairsByScope((prev) => ({
        ...prev,
        [nameScope]: toggleChannelDiffPair(prev[nameScope] || NO_PAIRS, a, b),
      }));
    },
    [nameScope],
  );

  const onFunctionChange = useCallback(
    (no: number, fn: LocationFunctionValue) => {
      setFunctionsByScope((prev) => ({
        ...prev,
        [nameScope]: { ...(prev[nameScope] || NO_FUNCTIONS), [no]: fn },
      }));
    },
    [nameScope],
  );

  const pourPartnersFor = useCallback((no: number) => pourPartners[no] || NO_KEYS, [pourPartners]);

  const onDiffChange = useCallback(
    (no: number, ids: ReadonlyArray<string>) => {
      const siblings = new Set<number>();
      const keys: string[] = [];
      ids.forEach((id) => {
        const sib = siblingNoFromDiffId(id);
        if (sib == null) keys.push(id);
        else if (sib !== no) siblings.add(sib);
      });
      // Pairs with the other ticked channels follow the ticks; pairs with
      // unticked channels stay remembered for the next device.
      setPairsByScope((prev) => {
        let pairs = prev[nameScope] || NO_PAIRS;
        rows
          .filter((r) => r.checked && r.no !== no)
          .forEach((r) => {
            if (hasChannelDiffPair(pairs, no, r.no) !== siblings.has(r.no)) {
              pairs = toggleChannelDiffPair(pairs, no, r.no);
            }
          });
        return { ...prev, [nameScope]: pairs };
      });
      setPourPartnersOf((prev) => {
        const byNo = prev.loggerId === loggerId ? prev.byNo : {};
        return { loggerId, byNo: { ...byNo, [no]: keys } };
      });
    },
    [nameScope, rows, loggerId],
  );

  const errors = useMemo(() => (loggerId ? channelBatchErrors(rows) : []), [loggerId, rows]);

  const validate = useCallback((): string[] => {
    setShowErrors(true);
    return errors;
  }, [errors]);

  // Pairs and channel Functions live in their own per-scope state, so
  // neither reset touches them.
  const resetForNextDevice = useCallback(() => {
    setNamesByScope({});
    setUntickedFor({ loggerId: '', nos: [] });
    setPourPartnersOf({ loggerId: '', byNo: {} });
    setShowErrors(false);
  }, []);

  const pickerProps: ChannelBatchPickerProps = {
    rows,
    onChannelNameChange,
    onCheckedChange,
    deviceSelected: !!loggerId,
    showErrors,
    isPairSelected,
    pourPartnersFor,
    onDiffChange,
    onFunctionChange,
  };

  return {
    rows,
    selected,
    showErrors,
    differentialPairs,
    isPairSelected,
    onPairToggle,
    pourPartnersFor,
    onDiffChange,
    onFunctionChange,
    pickerProps,
    errors,
    validate,
    resetForNextDevice,
    reset: resetForNextDevice,
  };
};

export default useChannelBatch;
